<template>
  <v-container>
    <div>
      <v-btn class="mb-3" @click="goBack" prepend-icon="mdi-arrow-left" text="Back to List" />
    </div>

    <v-row>
      <v-col cols="12">
        <div class="text-h6 mb-4">Manage System Instructions</div>
      </v-col>
    </v-row>

    <v-row>
      <!-- Description Column -->
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Description Instructions</span>
            <v-btn color="grey-darken-1" variant="text" size="small" prepend-icon="mdi-plus" @click="openAddDialog('description')">Add Instruction</v-btn>
          </v-card-title>
          <v-card-text>
            <v-list lines="two" class="mt-3">
              <template v-if="isLoading || isLoadingDescription">
                <div class="text-center py-4">
                  <v-progress-circular indeterminate />
                  <div class="mt-2">Loading instructions...</div>
                </div>
              </template>
              <template v-else-if="descriptionList.length === 0">
                <div class="pa-4 text-medium-emphasis">No description instructions found. Create your first instruction to get started.</div>
              </template>
              <template v-else>
                <v-list-item v-for="item in descriptionList" :key="item.id" :subtitle="truncate(item.content)" class="border rounded mb-2">
                <template #title>
                  <div class="d-flex align-center">
                    <span class="mr-2">{{ item.title }}</span>
                    <v-chip v-if="item.isActive" size="x-small" color="success" label>Active</v-chip>
                  </div>
                </template>
                <template #append>
                  <v-tooltip text="View">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" icon="mdi-magnify" size="small" variant="text" @click="openView(item)"></v-btn>
                    </template>
                  </v-tooltip>
                  <v-menu v-if="!item.isActive || !isDefault(item)" location="bottom end">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" icon="mdi-dots-vertical" size="small" variant="text"></v-btn>
                    </template>
                    <v-list density="compact" class="pa-2">
                      <v-list-item v-if="!item.isActive" @click="setActive(item)" class="mb-1" density="compact">
                        <template #prepend>
                          <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-check-circle</v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-medium-emphasis">Set Active</v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="!isDefault(item)" @click="editItem(item)" class="mb-1" density="compact">
                        <template #prepend>
                          <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-pencil</v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-medium-emphasis">Edit</v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="!isDefault(item)" @click="confirmDelete(item)" density="compact">
                        <template #prepend>
                          <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-delete</v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-medium-emphasis">Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
                </v-list-item>
              </template>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Highlight Column -->
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Highlight Instructions</span>
            <v-btn color="grey-darken-1" variant="text" size="small" prepend-icon="mdi-plus" @click="openAddDialog('highlight')">Add Instruction</v-btn>
          </v-card-title>
          <v-card-text>
            <v-list lines="two" class="mt-3">
              <template v-if="isLoading || isLoadingHighlight">
                <div class="text-center py-4">
                  <v-progress-circular indeterminate />
                  <div class="mt-2">Loading instructions...</div>
                </div>
              </template>
              <template v-else-if="highlightList.length === 0">
                <div class="pa-4 text-medium-emphasis">No highlight instructions found. Create your first instruction to get started.</div>
              </template>
              <template v-else>
                <v-list-item v-for="item in highlightList" :key="item.id" :subtitle="truncate(item.content)" class="border rounded mb-2">
                <template #title>
                  <div class="d-flex align-center">
                    <span class="mr-2">{{ item.title }}</span>
                    <v-chip v-if="item.isActive" size="x-small" color="success" label>Active</v-chip>
                  </div>
                </template>
                <template #append>
                  <v-tooltip text="View">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" icon="mdi-magnify" size="small" variant="text" @click="openView(item)"></v-btn>
                    </template>
                  </v-tooltip>
                  <v-menu v-if="!item.isActive || !isDefault(item)" location="bottom end">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" icon="mdi-dots-vertical" size="small" variant="text"></v-btn>
                    </template>
                    <v-list density="compact" class="pa-2">
                      <v-list-item v-if="!item.isActive" @click="setActive(item)" class="mb-1" density="compact">
                        <template #prepend>
                          <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-check-circle</v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-medium-emphasis">Set Active</v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="!isDefault(item)" @click="editItem(item)" class="mb-1" density="compact">
                        <template #prepend>
                          <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-pencil</v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-medium-emphasis">Edit</v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="!isDefault(item)" @click="confirmDelete(item)" density="compact">
                        <template #prepend>
                          <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-delete</v-icon>
                        </template>
                        <v-list-item-title class="text-body-2 text-medium-emphasis">Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
                </v-list-item>
              </template>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="720px">
      <v-card>
        <v-card-title>{{ editingItem ? 'Edit Instruction' : 'Add New Instruction' }}</v-card-title>
        <v-card-text>
          <v-form v-model="isFormValid" @submit.prevent="save">
            <v-text-field v-model="form.title" label="Title" :rules="[v => !!v || 'Title is required']" variant="outlined" class="mb-3" />
            <v-textarea v-model="form.content" label="Content" :rows="10" :rules="[v => !!v || 'Content is required']" variant="outlined" class="mb-3" />
            <v-checkbox v-model="form.isActive" label="Set as active" />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text="Cancel" @click="closeDialog" />
          <v-btn color="primary" text="Save" :loading="isSaving" :disabled="!isFormValid" @click="save" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Dialog -->
    <v-dialog v-model="showViewDialog" max-width="800px">
      <v-card class="view-card">
        <v-card-title class="d-flex justify-space-between align-center sticky-header">
          <span>{{ viewingItem?.title }}</span>
          <v-btn size="small" variant="text" icon="mdi-content-copy" @click="copyViewing"></v-btn>
        </v-card-title>
        <v-card-text class="scroll-body">
          <v-textarea
            :model-value="viewingPlainText"
            readonly
            auto-grow
            rows="12"
            variant="outlined"
            class="plain-text"
          />
        </v-card-text>
        <v-card-actions class="sticky-footer">
          <v-spacer />
          <v-btn text="Close" @click="showViewDialog = false" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Delete Instruction</v-card-title>
        <v-card-text>Are you sure you want to delete "{{ deletingItem?.title }}"?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text="Cancel" @click="showDeleteDialog = false" />
          <v-btn color="error" text="Delete" :loading="isSaving" @click="performDelete" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbars -->
    <v-snackbar v-model="showError" color="error" timeout="5000">{{ errorMessage }}</v-snackbar>
    <v-snackbar v-model="showSuccess" color="success" timeout="3000">{{ successText }}</v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { listSystemInstructions, createSystemInstruction, updateSystemInstruction, deleteSystemInstruction } from '@/apis'

interface Props { goBack?: () => void }
const props = withDefaults(defineProps<Props>(), { goBack: undefined })

type TType = 'description' | 'highlight'
const activeTab = ref<TType>('description')
const isLoading = ref(false)
const isSaving = ref(false)
const isLoadingDescription = ref(false)
const isLoadingHighlight = ref(false)
const isFormValid = ref(false)
const showDialog = ref(false)
const showDeleteDialog = ref(false)
const showViewDialog = ref(false)
const showError = ref(false)
const showSuccess = ref(false)
const successText = ref('Saved')
const errorMessage = ref('')

const descriptionList = ref<any[]>([])
const highlightList = ref<any[]>([])

const form = ref<{ title: string; content: string; isActive: boolean }>({ title: '', content: '', isActive: false })
const editingItem = ref<any | null>(null)
const deletingItem = ref<any | null>(null)
const viewingItem = ref<any | null>(null)
const dedent = (s: string): string => {
  const lines = String(s || '').replace(/\t/g, '  ').split('\n')
  let min = Infinity
  for (const line of lines) {
    if (!line.trim()) continue
    const m = line.match(/^ */)
    const n = m ? m[0].length : 0
    if (n < min) min = n
  }
  if (!isFinite(min)) min = 0
  return lines.map(l => l.slice(min)).join('\n')
}
const normalizeBullets = (s: string): string => s.replace(/^[ \t]{2,}\*\s+/gm, '* ')
const viewingPlainText = computed(() => normalizeBullets(dedent(String(viewingItem.value?.content || ''))))

const currentList = computed(() => (activeTab.value === 'description' ? descriptionList.value : highlightList.value))

const truncate = (s: string, n = 120) => {
  const t = globalThis.String(s ?? '')
  return t.length > n ? t.slice(0, n) + '…' : t
}

const isDefault = (item: any) => globalThis.String(item?.id ?? '').startsWith('default-')
const copyContent = (item: any) => {
  try {
    navigator.clipboard.writeText(globalThis.String(item?.content ?? ''))
    successText.value = 'Copied'
    showSuccess.value = true
  } catch (e) {
    errorMessage.value = 'Copy failed'
    showError.value = true
  }
}

const openView = (item: any) => {
  viewingItem.value = item
  showViewDialog.value = true
}

const copyViewing = () => copyContent(viewingItem.value)

const load = async () => {
  isLoading.value = true
  isLoadingDescription.value = true
  isLoadingHighlight.value = true
  try {
    const [d, h] = await Promise.all([
      listSystemInstructions('description'),
      listSystemInstructions('highlight'),
    ])
    descriptionList.value = d
    highlightList.value = h
  } catch (e: any) {
    errorMessage.value = 'Failed to load system instructions'
    showError.value = true
  } finally {
    isLoading.value = false
    isLoadingDescription.value = false
    isLoadingHighlight.value = false
  }
}

const loadType = async (type: TType) => {
  if (type === 'description') isLoadingDescription.value = true; else isLoadingHighlight.value = true;
  try {
    const list = await listSystemInstructions(type)
    if (type === 'description') descriptionList.value = list; else highlightList.value = list;
  } catch (e: any) {
    errorMessage.value = 'Failed to load system instructions'
    showError.value = true
  } finally {
    if (type === 'description') isLoadingDescription.value = false; else isLoadingHighlight.value = false;
  }
}

const openAddDialog = (presetType?: TType) => {
  if (presetType) activeTab.value = presetType
  editingItem.value = null
  form.value = { title: '', content: '', isActive: false }
  showDialog.value = true
}

const editItem = (item: any) => {
  editingItem.value = item
  form.value = { title: item.title, content: item.content, isActive: item.isActive }
  showDialog.value = true
}

const closeDialog = () => {
  showDialog.value = false
  editingItem.value = null
  form.value = { title: '', content: '', isActive: false }
}

const save = async () => {
  if (!isFormValid.value) return
  isSaving.value = true
  try {
    const type: TType = activeTab.value
    if (editingItem.value) {
      await updateSystemInstruction(editingItem.value.id, { title: form.value.title, content: form.value.content, isActive: form.value.isActive })
    } else {
      await createSystemInstruction({ title: form.value.title, content: form.value.content, type, isActive: form.value.isActive })
    }
    await load()
    successText.value = 'Saved'
    showSuccess.value = true
    closeDialog()
  } catch (e: any) {
    errorMessage.value = 'Failed to save instruction'
    showError.value = true
  } finally {
    isSaving.value = false
  }
}

const setActive = async (item: any) => {
  isSaving.value = true
  const t = (String((item as any)?.type || '').toLowerCase() as TType)
  if (t === 'description') isLoadingDescription.value = true; else if (t === 'highlight') isLoadingHighlight.value = true;
  try {
    await updateSystemInstruction(item.id, { isActive: true })
    if (t === 'description' || t === 'highlight') await loadType(t); else await load();
  } catch (e: any) {
    errorMessage.value = 'Failed to set active instruction'
    showError.value = true
  } finally {
    isSaving.value = false
    if (t === 'description') isLoadingDescription.value = false; else if (t === 'highlight') isLoadingHighlight.value = false;
  }
}

const confirmDelete = (item: any) => {
  deletingItem.value = item
  showDeleteDialog.value = true
}

const performDelete = async () => {
  if (!deletingItem.value) return
  isSaving.value = true
  try {
    await deleteSystemInstruction(deletingItem.value.id)
    await load()
    showDeleteDialog.value = false
    deletingItem.value = null
  } catch (e: any) {
    errorMessage.value = 'Failed to delete instruction'
    showError.value = true
  } finally {
    isSaving.value = false
  }
}

const goBack = () => { if (props.goBack) props.goBack() }

onMounted(load)
</script>

<style scoped>
.border { border: 1px solid rgba(0,0,0,0.12); }
.view-card { max-height: 80vh; display: flex; flex-direction: column; }
.scroll-body { overflow-y: auto; overflow-x: hidden; flex: 1 1 auto; }
.sticky-header { position: sticky; top: 0; z-index: 2; background: rgb(var(--v-theme-surface)); }
.sticky-footer { position: sticky; bottom: 0; z-index: 2; background: rgb(var(--v-theme-surface)); }
.markdown-body :where(h1,h2,h3){ margin: 0.5rem 0 0.25rem; font-weight: 600; }
.markdown-body p{ margin: 0.25rem 0 0.75rem; }
.markdown-body ul, .markdown-body ol{ padding-left: 1.25rem; margin: 0.25rem 0 0.75rem; }
.markdown-body code { background: rgba(0,0,0,0.04); padding: 0.1rem 0.25rem; border-radius: 4px; }
</style>


